/**
 * Shared form-creation helpers used by the onboarding wizard.
 * FormBuilderList has its own inline createNewForm — that stays untouched.
 */

import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Returns white or dark text depending on which has better contrast against
 * the given brand hex color (WCAG relative luminance threshold of 0.5).
 */
export function getButtonTextColor(hex) {
  const h = hex.replace(/^#/, '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const toLinear = c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const lum = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return lum < 0.5 ? '#ffffff' : '#1e293b';
}

const DEFAULT_WIZARD_THEME = {
  primaryColor: '#171A0D',
  backgroundColor: '#ffffff',
  textColor: '#333333',
  borderColor: '#dddddd',
  borderRadius: '8px',
  buttonColor: '#171A0D',
  buttonTextColor: '#ffffff',
};

const WIZARD_FIELDS = [
  {
    id: 'email',
    type: 'email',
    label: 'Email Address',
    name: 'email',
    placeholder: 'you@example.com',
    required: true,
    order: 0,
  },
  {
    id: 'name',
    type: 'text',
    label: 'Full Name',
    name: 'name',
    placeholder: 'Your name',
    required: false,
    order: 1,
  },
  {
    id: 'website',
    type: 'text',
    label: 'Website',
    name: 'website',
    placeholder: 'https://yoursite.com',
    required: false,
    order: 2,
  },
];

/**
 * Create a wizard-style form in Firestore.
 * Returns the new form's document ID.
 * NOTE: Task 6 (brand-color extraction) will pass a `theme` override here.
 */
export async function createWizardForm(workspaceId, { businessName, theme, createdBy } = {}) {
  const name = businessName ? `${businessName} Contact Form` : 'Contact Form';

  const formData = {
    workspaceId,
    createdBy,
    name,
    fields: WIZARD_FIELDS,
    settings: {
      spamDetection: true,
      spamRedirect: true,
      aiTesting: false,
      autoReply: false,
      scheduleMeetings: false,
      trackingPlatforms: [],
      autoUpdateTracking: false,
      // Reply "Shown as" name — never form.name. See src/utils/replyIdentity.js.
      businessName: businessName || null,
    },
    theme: theme || DEFAULT_WIZARD_THEME,
    layout: 'modern',
    embedCode: '',
    trackingPixels: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  const docRef = await addDoc(collection(db, 'forms'), formData);
  return docRef.id;
}
